<template>
  <div class="multi-select">
    <div
      class="field d-flex flex-wrap"
      :class="{
        empty: values.length == 0,
        errored: error,
        focussed: isSearchFocussed,
        'no-border': hideBorder
      }"
    >
      <span class="label" v-if="label">{{ label }}</span>
      <template v-for="(value, index) in values">
        <div :key="index" class="d-contents">
          <with-close-icon
            class="chip-container d-flex align-items-center"
            :wrapper-style="{ maxWidth: chipMaxWidth }"
            @close="removeItem(index)"
          >
            <div class="chip text-medium text-truncate" v-no-capture>
              <template v-if="$scopedSlots.default">
                <slot v-bind:value="value"> </slot>
              </template>
              <span class="pl-3 text-truncate" :title="value.label" v-else>{{ value.label }}</span>
            </div>
          </with-close-icon>
          <div
            class="item-between-pills text-secondary text-medium"
            v-if="textBetweenPills && index !== values.length - 1"
          >
            {{ textBetweenPills }}
          </div>
        </div>
      </template>
      <input
        class="text-medium plain"
        @keyup.enter="onEnterPress"
        @keyup.down="onDownKeyPress"
        @keyup.up="onUpKeyPress"
        @keydown.esc="onEscape"
        @keydown.delete="prevSearchText = searchText"
        @keyup.delete="onDeletePress"
        @keyup="$event => ($refs.search.style.width = `${$event.target.value.length * 14}px`)"
        ref="search"
        v-model="searchText"
        type="text"
        @focus="onSearchFocus"
        @blur="onSearchBlur"
        :id="id"
        :placeholder="values.length > 0 ? '' : placeholder"
      />
      <label :for="id" class="flex-grow-1"></label>
    </div>
    <div class="field-error-message pl-3" v-if="error">{{ error }}</div>
    <div
      class="menu menu-shadow text-medium"
      :class="{ [menuAlignment]: true }"
      :style="{ width: `${menuWidth}px` }"
      v-if="showDropdown"
      @keydown.esc="toggleDropdown(false)"
      :data-preventBlur="true"
      tabindex="-1"
    >
      <ul @click="$refs.search.focus()">
        <div class="flex-center my-4" v-if="loading">
          <spinner />
        </div>
        <span class="item text-medium text-secondary" v-else-if="dropdownOptions.length === 0">
          <slot name="noResultMessage" v-if="$slots.noResultMessage"></slot>
          <template v-else>
            {{ noResultMessage }}
          </template>
        </span>
        <template v-else>
          <li
            class="item selectable"
            v-for="(option, index) in dropdownOptions"
            :key="index"
            @click="selectItem(index)"
            :data-index="index"
            v-no-capture
          >
            <template v-if="$scopedSlots.listItem">
              <slot name="listItem" v-bind:option="option"></slot>
            </template>
            <span class="item-text text-truncate" :title="option.label" v-else>{{
              option.label
            }}</span>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/Spinner'
import WithCloseIcon from '@/components/WithCloseIcon'
import debounce from '@/utils/debounce'
import scrollToElement from '@/utils/scrollToElement'
import * as R from 'ramda'

/**
 * This component can be used as a form field to select multiple items.
 * Selected items are shown as pills
 *
 * props:
 *
 *  - options: The options to be shown in dropdown list
 *    - Each item inside options must have a key: label. Multiselect uses this to filter result based on user search
 *  - values: It is an array of selected values from options
 *
 *  Note that options and values should be mutually exclusive
 *
 *  - placeholder: placeholder for search field. It is only shown when there are no items selected
 *
 *  Emits:
 *
 *  - change: This event is emitted when values are selected or removed. Entire selected list is passed as argument
 *
 * The way pills and Dropdown list items can be shown are customizable using slots
 *
 * Slots:
 *
 * - default: This slot can be used to provide template to the selected items (pills)
 *
 * - listItem: This slot can be used to provide template to the dropdown list items
 */
export default {
  name: 'MultiSelect',
  props: {
    values: { type: Array },
    textBetweenPills: String,
    hideBorder: Boolean,
    options: {
      default: () => [],
      type: Array
    },
    placeholder: String,
    loading: Boolean,
    /*
    align menu to left/right of the field
     */
    menuAlignment: { type: String, default: 'left' },
    menuWidth: Number,
    label: String,
    error: String,
    chipMaxWidth: {
      type: String,
      default: '300px'
    },
    noResultMessage: {
      type: String,
      default: 'Nothing here...'
    }
  },
  data() {
    return {
      searchText: '',
      prevSearchText: '',
      showDropdown: false,
      isSearchFocussed: false,
      id: Math.random()
    }
  },
  components: {
    WithCloseIcon,
    Spinner
  },
  computed: {
    dropdownOptions() {
      return this.options.filter(
        x => x.label.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
      )
    }
  },
  methods: {
    onSearchFocus() {
      this.isSearchFocussed = true
      this.toggleDropdown(true)
    },
    onSearchBlur() {
      this.isSearchFocussed = false
      this.toggleDropdown(false)
    },
    toggleDropdown: debounce(function(show) {
      this.showDropdown = show
    }, 200),
    selectFirst() {
      const firstItem = this.$el.querySelector('.selectable')
      firstItem && firstItem.classList.add('selected')
    },
    selectLast() {
      const allSelectableItems = this.$el.querySelectorAll('.selectable')
      allSelectableItems[allSelectableItems.length - 1].classList.add('selected')
    },
    scrollToSelectedElement() {
      const selected = this.$el.querySelector('.selectable.selected')
      selected && scrollToElement(selected)
    },
    onDownKeyPress() {
      const selected = this.$el.querySelector('.selectable.selected')
      if (selected) {
        selected.classList.remove('selected')
        const nextSibling = selected.nextElementSibling
        if (nextSibling) {
          nextSibling.classList.add('selected')
        } else {
          this.selectFirst()
        }
      } else {
        this.selectFirst()
      }
      this.scrollToSelectedElement()
    },
    onUpKeyPress() {
      const selected = this.$el.querySelector('.selectable.selected')
      if (selected) {
        selected.classList.remove('selected')
        const prevSibling = selected.previousElementSibling
        if (prevSibling) {
          prevSibling.classList.add('selected')
        } else {
          this.selectLast()
        }
      } else {
        this.selectLast()
      }
      this.scrollToSelectedElement()
    },
    selectItem(index) {
      this.$emit('change', [...this.values, this.dropdownOptions[index]])
      this.searchText = ''
    },
    onEnterPress() {
      const selected = this.$el.querySelector('.selectable.selected')
      if (selected) {
        const index = +selected.dataset.index
        this.selectItem(index)
      }
    },
    onDeletePress(event) {
      if (
        event.target.selectionStart === 0 &&
        event.target.selectionStart === 0 &&
        !this.searchText &&
        !this.prevSearchText
      ) {
        this.$emit('change', R.init(this.values))
      }
    },
    removeItem(index) {
      this.$emit('change', R.remove(index, 1, this.values))
    },
    onEscape(ev) {
      if (this.showDropdown) {
        this.toggleDropdown(false)
        ev.stopPropagation()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/_errors.scss';
@import '~@/styles/components/input';

.multi-select {
  position: relative;
}
.menu {
  position: absolute;
  border-radius: 5px;
  top: calc(100% + 5px);
  left: 0px;
  background-color: white;
  width: 200px;
  padding: 10px 0;
  z-index: 1000;
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
}
.menu.right {
  right: 0;
  left: auto;
}
ul {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    cursor: pointer;
  }
}
.item {
  display: flex;
  padding: 5px 10px;
  &:hover {
    background-color: $quinary;
  }
}

.item-text {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 2px 4px;
}

.selectable.selected {
  background-color: $quinary;
}

.chip-container {
  margin-right: 4px;
  margin-bottom: 6px;
  margin-top: 2px;
  background-color: $quaternary;
  color: $primary;
  border-radius: 20px;
  overflow: hidden;
}

.chip {
  display: flex;
  align-items: center;
  border-radius: 20px;
  padding: 2px 2px 2px 0;
}
.field {
  border: 1px solid $tertiary;
  background-color: $quinary;
  border-radius: 20px;
  padding: 4px 10px 0 6px;
  overflow: hidden;
  &:not(.no-border) {
    border-color: transparent;
  }
}
.multi-select .field.focussed {
  border-color: $prime;
}
.label {
  font-size: 0.875rem;
  margin-top: 4px;
  padding: 0 6px;
}
input {
  display: inline-block;
  height: 28px;
  margin-bottom: 2px;
  min-width: 20px;
  border-radius: 10px;
  max-width: 90px;
  width: 20px;
}
.empty input {
  min-width: 135px;
  padding-left: 10px;
}
.errored {
  border-color: $error;
}
.d-contents {
  display: contents;
}
.item-between-pills {
  align-self: center;
  margin-right: 4px;
}
</style>
